import { AuthMachineStatus } from "../../constants";
import type { AuthMachineEvent, AuthMachineState } from "./types";
import { isPasswordResetRequired } from "./types";

/** Exhaustiveness backstop: fails loudly if a future AuthMachineEvent variant is
 *  added without a corresponding case in reduce()'s switch, instead of silently
 *  returning undefined. */
const assertNever = (value: never): never => {
  throw new Error(`[authMachine] unhandled event type: ${JSON.stringify(value)}`);
};

const transition = (
  state: AuthMachineState,
  next: Partial<AuthMachineState> & Pick<AuthMachineState, "status">
): AuthMachineState => ({
  ...state,
  isFreshSignIn: false,
  passwordResetFlagWritePending: false,
  ...next,
  generation: state.generation + 1,
});

/** Destination for any event that lands a user: reset-required users go to the reset screen. */
const enterWithUser = (
  state: AuthMachineState,
  user: NonNullable<AuthMachineState["user"]>,
  opts: { freshSignIn?: boolean; forceReset?: boolean } = {}
): AuthMachineState => {
  if (opts.forceReset || isPasswordResetRequired(user)) {
    return transition(state, {
      status: AuthMachineStatus.PASSWORD_RESET_PENDING,
      user,
      // Already-flagged users (metadata flag true) need no server write.
      passwordResetFlagWritePending: !isPasswordResetRequired(user),
    });
  }
  return transition(state, {
    status: AuthMachineStatus.AUTHENTICATED,
    user,
    isFreshSignIn: opts.freshSignIn ?? false,
  });
};

export const reduce = (state: AuthMachineState, event: AuthMachineEvent): AuthMachineState => {
  const inAuthedFamily =
    state.status === AuthMachineStatus.AUTHENTICATED ||
    state.status === AuthMachineStatus.PASSWORD_RESET_PENDING;
  const inEntryState =
    state.status === AuthMachineStatus.BOOTING ||
    state.status === AuthMachineStatus.UNAUTHENTICATED ||
    state.status === AuthMachineStatus.EXPIRED;

  switch (event.type) {
    case "BOOT_COMPLETED":
      if (state.status !== AuthMachineStatus.BOOTING) return state;
      return event.user
        ? enterWithUser(state, event.user)
        : transition(state, { status: AuthMachineStatus.UNAUTHENTICATED, user: null });

    case "BOOT_SESSION_EXPIRED":
      if (state.status !== AuthMachineStatus.BOOTING) return state;
      return transition(state, { status: AuthMachineStatus.EXPIRED, user: null });

    case "SIGNED_IN":
      if (inAuthedFamily) {
        // Same-user re-confirmation (tab refocus): context-only update, no transition.
        if (state.user && state.user.id === event.user.id) {
          return { ...state, user: event.user };
        }
        return enterWithUser(state, event.user, { freshSignIn: event.freshSignIn });
      }
      if (inEntryState) {
        return enterWithUser(state, event.user, { freshSignIn: event.freshSignIn });
      }
      return state;

    case "RECOVERY_SIGNED_IN":
      if (!inEntryState && !inAuthedFamily) return state;
      return enterWithUser(state, event.user, { forceReset: true });

    case "EMAIL_CONFIRMED":
      if (!inEntryState) return state;
      return enterWithUser(state, event.user);

    case "USER_REFRESHED":
      if (!inAuthedFamily) return state;
      return { ...state, user: event.user };

    case "PASSWORD_RESET_ENTERED":
      if (state.status !== AuthMachineStatus.AUTHENTICATED || !state.user) return state;
      return transition(state, {
        status: AuthMachineStatus.PASSWORD_RESET_PENDING,
        passwordResetFlagWritePending: !isPasswordResetRequired(state.user),
      });

    case "PASSWORD_UPDATED":
      if (
        state.status !== AuthMachineStatus.PASSWORD_RESET_PENDING ||
        event.generation !== state.generation
      ) {
        return state;
      }
      return transition(state, { status: AuthMachineStatus.AUTHENTICATED, user: event.user });

    case "PASSWORD_UPDATE_FAILED":
      // Both branches return `state` today (no-op in effect) — the guard is kept anyway
      // so a future non-trivial failure handler slots in without re-deriving the check,
      // and so this case stays structurally uniform with the other three stamped events.
      if (
        state.status !== AuthMachineStatus.PASSWORD_RESET_PENDING ||
        event.generation !== state.generation
      ) {
        return state;
      }
      return state;

    case "PASSWORD_RESET_FLAG_WRITE_SUCCEEDED":
      if (
        state.status !== AuthMachineStatus.PASSWORD_RESET_PENDING ||
        event.generation !== state.generation
      ) {
        return state;
      }
      return { ...state, passwordResetFlagWritePending: false, user: event.user };

    case "PASSWORD_RESET_FLAG_WRITE_FAILED":
      if (
        state.status !== AuthMachineStatus.PASSWORD_RESET_PENDING ||
        event.generation !== state.generation
      ) {
        return state;
      }
      return { ...state, passwordResetFlagWritePending: true };

    case "SIGN_IN_MARKED_COMPLETE":
      if (!inAuthedFamily || !state.isFreshSignIn) return state;
      return { ...state, isFreshSignIn: false };

    case "SIGNED_OUT":
      if (!inAuthedFamily) return state;
      return transition(state, {
        status: event.deliberate ? AuthMachineStatus.UNAUTHENTICATED : AuthMachineStatus.EXPIRED,
        user: null,
      });

    case "SESSION_DIED":
      if (!inAuthedFamily) return state;
      return transition(state, { status: AuthMachineStatus.EXPIRED, user: null });

    default:
      return assertNever(event);
  }
};
